import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { SymbolGrid } from "@/components/SymbolGrid";
import { getSymbolName, codepointLabel } from "@/data/symbol-names";
import { symbolSlug } from "@/data/symbols";
import { symbolAriaLabel } from "@/lib/symbol-aria";


interface CategoryExplorerProps {
  symbols: string[];
  categoryName: string;
  /** Optional Tailwind text-color class to tint the category's glyphs. */
  accentClass?: string;
}

interface Item {
  symbol: string;
  slug: string;
  name: string;
  code: string;
}

export function CategoryExplorer({ symbols, categoryName, accentClass = "" }: CategoryExplorerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [liveMsg, setLiveMsg] = useState("");

  const blurTimer = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const liveTimer = useRef<number | null>(null);

  const navigate = useNavigate();
  const reactId = useId();
  const inputId = `symbol-search-${reactId}`;
  const listboxId = `symbol-search-listbox-${reactId}`;
  const helpId = `symbol-search-help-${reactId}`;
  const statusId = `symbol-search-status-${reactId}`;

  const items: Item[] = useMemo(
    () =>
      symbols.map((s) => ({
        symbol: s,
        slug: symbolSlug(s),
        name: getSymbolName(s),
        code: codepointLabel(s),
      })),
    [symbols],
  );

  // Category-specific search examples (not the same hardcoded "heart / ★ / u+2605"
  // on every page — that was duplicate content). The keyword is the category's own
  // subject word (so typing it actually matches), and the character + codepoint
  // come from this category's first symbol.
  const examples = useMemo(() => {
    const first = items[0];
    const keyword =
      categoryName
        .replace(/\s+(symbols?|combos?|signs?|marks?|characters?)$/i, "")
        .trim()
        .split(/\s+/)[0]
        .toLowerCase() || "name";
    return {
      keyword,
      char: first?.symbol ?? "",
      code: (first?.code ?? "").split(" ")[0].toLowerCase(),
    };
  }, [items, categoryName]);

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter(
      (it) =>
        it.symbol.includes(query) ||
        it.name.toLowerCase().includes(q) ||
        it.code.toLowerCase().includes(q),
    );
  }, [items, q, query]);

  const suggestions = useMemo(() => (q ? filtered.slice(0, 8) : []), [filtered, q]);
  const filteredSymbols = filtered.map((i) => i.symbol);
  const listboxOpen = open && suggestions.length > 0;

  // Announce result counts to screen readers (debounced so typing isn't spammy).
  useEffect(() => {
    if (liveTimer.current) window.clearTimeout(liveTimer.current);
    if (!q) {
      setLiveMsg("");
      return;
    }
    liveTimer.current = window.setTimeout(() => {
      if (suggestions.length === 0) {
        setLiveMsg(`No suggestions for ${query}.`);
      } else {
        const top = suggestions[0];
        setLiveMsg(
          `${suggestions.length} ${suggestions.length === 1 ? "suggestion" : "suggestions"} available. Top result: ${symbolAriaLabel(top)} Use Up and Down arrows to navigate, Enter to open.`,
        );
      }
    }, 250);
    return () => {
      if (liveTimer.current) window.clearTimeout(liveTimer.current);
    };
  }, [q, query, suggestions]);

  // Scroll the active option into view.
  useEffect(() => {
    if (!listboxOpen || activeIdx < 0) return;
    const node = optionRefs.current[activeIdx];
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [activeIdx, listboxOpen]);

  function closeList() {
    setOpen(false);
    setActiveIdx(-1);
  }

  function goTo(it: Item) {
    closeList();
    setQuery("");
    setLiveMsg(`Opening ${it.name}.`);
    navigate({ to: "/symbol/$slug", params: { slug: it.slug } });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      if (!open) {
        setOpen(true);
        return;
      }
      if (suggestions.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Home" && listboxOpen) {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End" && listboxOpen) {
      e.preventDefault();
      setActiveIdx(suggestions.length - 1);
    } else if (e.key === "Enter") {
      if (!listboxOpen) return;
      const pick = activeIdx >= 0 ? suggestions[activeIdx] : suggestions[0];
      if (pick) {
        e.preventDefault();
        goTo(pick);
      }
    } else if (e.key === "Escape") {
      if (listboxOpen) {
        e.preventDefault();
        closeList();
      } else if (query) {
        e.preventDefault();
        setQuery("");
      }
    } else if (e.key === "Tab") {
      closeList();
    }
  }

  const activeOptionId =
    listboxOpen && activeIdx >= 0 && suggestions[activeIdx]
      ? `sugg-${reactId}-${suggestions[activeIdx].slug}`
      : undefined;

  return (
    <>
      <div className="relative mb-4">
        <label htmlFor={inputId} className="sr-only">
          Search {categoryName.toLowerCase()} by name, character, or Unicode codepoint
        </label>
        <div
          role="combobox"
          aria-haspopup="listbox"
          aria-owns={listboxId}
          aria-expanded={listboxOpen}
          className="glass flex items-center gap-2 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-ring"
        >
          <span aria-hidden="true" className="text-muted-foreground">⌕</span>
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            inputMode="search"
            autoComplete="off"
            spellCheck={false}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            aria-describedby={`${helpId} ${statusId}`}
            placeholder={`Search ${categoryName.toLowerCase()} by name, character or U+code…`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActiveIdx(-1);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={onKeyDown}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground sm:text-base"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                closeList();
                inputRef.current?.focus();
              }}
              className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Clear search"
            >
              Clear
            </button>
          )}
        </div>

        {/* Listbox is always rendered so AT can find it via aria-controls; hidden when empty. */}
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={`Suggested ${categoryName.toLowerCase()}`}
          hidden={!listboxOpen}
          onMouseDown={(e) => {
            // Prevent input blur before click registers.
            e.preventDefault();
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
          className="glass absolute left-0 right-0 top-full z-20 mt-2 max-h-80 overflow-auto rounded-xl p-1 shadow-lg"
        >
          {suggestions.map((it, i) => {
            const selected = i === activeIdx;
            return (
              <li
                key={it.slug}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                id={`sugg-${reactId}-${it.slug}`}
                role="option"
                aria-selected={selected}
                aria-label={symbolAriaLabel(it)}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => goTo(it)}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm ${
                  selected ? "bg-secondary text-foreground" : ""
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl ${accentClass}`}
                >
                  {it.symbol}
                </span>
                <span aria-hidden="true" className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{it.name}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {it.code}
                  </span>
                </span>
                <span aria-hidden="true" className="text-xs text-muted-foreground">
                  ↵
                </span>
              </li>
            );
          })}
        </ul>

        <p id={helpId} className="mt-2 text-xs text-muted-foreground">
          {q
            ? `${filtered.length} of ${items.length} match "${query}"`
            : `${items.length} symbols — try "${examples.keyword}", "${examples.char}" or "${examples.code}". Use arrow keys to navigate suggestions.`}
        </p>

        {/* Polite live region: announces suggestion counts and selections to screen readers. */}
        <div
          id={statusId}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {liveMsg}
        </div>
      </div>

      <div className="glass rounded-2xl p-3 sm:p-7">
        {filteredSymbols.length > 0 ? (
          <SymbolGrid symbols={filteredSymbols} size="lg" accentClass={accentClass} />
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No symbols match "{query}". Try a different term.
          </div>
        )}
      </div>

      <h2 className="mb-4 mt-10 font-display text-xl font-semibold">
        {q ? `Matching ${categoryName.toLowerCase()}` : `All ${categoryName.toLowerCase()} — details & copy`}
      </h2>
      {filtered.length > 0 ? (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((it) => (
            <li key={it.slug}>
              <Link
                to="/symbol/$slug"
                params={{ slug: it.slug }}
                className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={symbolAriaLabel(it)}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl ${accentClass}`}
                >
                  {it.symbol}
                </span>
                <span aria-hidden="true" className="min-w-0 flex-1">
                  <span className="block truncate">{it.name}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {it.code}
                  </span>
                </span>
                <span aria-hidden="true" className="text-muted-foreground">→</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing here — clear the search to see all symbols.</p>
      )}
    </>
  );
}
